import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { UserLiteData } from '../../../api/ApiTypes';
import { useLoader, useSubmitter } from '../../../platform/Hook';
import { Button, Colors, TypeaheadInput, TypeaheadItem } from '../../../ui';
import { LoadingGuard } from '../../../ui/LoadingGuard';
import { PillEditor, PillableItem } from '../../../ui/PillEditor';
import { ResultInfoForSubmitter } from '../../../ui/ResultInfo';
import { FixedScroller } from '../../../ui/Scrollers';
import { AppEvents } from '../../AppEvents';
import { useResolve } from '../../Container';
import { Copy, Flexer, Title } from '../elements/LayoutElements';

const workspaceHelp = `
    This will search through your organizations existing workspaces.  
    For new workspaces, they will need creating first in the Workspaces admin section.
`;

const InputWrapper = styled.div`
    position: relative;
`;

const InputEnter = styled.div`
    position: absolute;
    top: 50%;
    right: .5rem;
    transform: translateY(-50%);
    padding: 0.1em 0.3em;
    background-color: #f9f9f9;
    background-image: linear-gradient(to bottom,#eee,#f9f9f9,#eee);
    border: 1px solid #aaa;
    box-shadow: 0.1em 0.1em 0.2em rgb(0 0 0 / 10%);
    font-size: 0.75rem;
`;

const SelectMenuDesc = styled.span<{ disabled?: boolean }> `
    font-size: .875rem;
    color: ${p => p.disabled ? Colors.text : Colors.textLighter};
    margin-left: .25rem;
`;

const isValid = (email: string) => email.includes('@') && !email.match(/\s/);

// const [onInvite, working, result] = useSubmitter(async () => {
//     const values = selection.map(x => x.id);
//     const results = await api.exec(v2.workspaces.members.add({ workspaceId, values }));
//     for (const user of results) {
//         evts.push('workspace:members:add', { workspace, user });
//     }
//     action(true);
// }, [selection]);

// export const MemberInviteViaEmail = ({ workspaceId }: { workspaceId: string }) => {

//     const api = useResolve(ApiClient);
//     const [input, setInput] = useState('');
//     const [emails, setEmails] = useState([] as string[]);
//     const [type, setRole] = useState('member');
//     const [workspaces, setWorkspaces] = useState([] as WorkspaceLiteData[]);
//     const accessControl = useFeatureCheck('accessControl');
//     const [acTeamWrite] = usePermissions('ac_team:write');

//     const onReset = useCallback(() => {
//         setEmails([]);
//         setWorkspaces([]);
//     }, []);

//     const [onSubmit, working, result] = useSubmitter(async () => {
//         await api.exec(v2.admin.invites.send({ emails, type, workspaceIds: workspaces.map(x => x.id) }));
//         onReset();
//     }, [emails, type, workspaces]);

//     return <Flexer flex="column" gap="1">
//         <Flexer flex="column" gap=".25">
//             <Copy flush bold>
//                 To:
//             </Copy>
//             <InputWrapper>
//                 <Input
//                     placeholder="e.g. steve@metroretro.io"
//                     value={input}
//                     onChange={setInput}
//                     itemMode
//                     items={emails}
//                     onItemAdd={values => setEmails(x => Arr.unique([...x, ...values]).filter(x => isValid(x)))}
//                     onItemRemove={values => setEmails(x => x.filter(y => !values.includes(y)))}
//                 />
//                 {input.length > 0 && <InputEnter>&crarr; Enter</InputEnter>}
//             </InputWrapper>
//         </Flexer>
//         <Flexer flex="row-reverse" align="center" justify="space-between" margin=".5 0 0 0">
//             <ButtonStrip align="right">
//                 <Button onAction={onReset}>Reset</Button>
//                 <Button onAction={onSubmit} disabled={working || !emails.length} variant="primary">
//                     {working ? 'Sending...' : `Send Invite${emails.length > 1 ? 's' : ''} ${emails.length > 0 ? `(${emails.length})` : ''}`}
//                     {working && <Loader
//                         type="TailSpin"
//                         color="#fff"
//                         height={remInPx(1.2)}
//                         width={remInPx(1.2)}
//                     />}
//                 </Button>
//             </ButtonStrip>
//             <ResultInfoForSubmitter result={result} onSuccess="Invitations have been sent!" />
//         </Flexer>
//     </Flexer>;
// };

const emailPattern = /.*@.*\..*/;

const toPillables = (item: TypeaheadItem) => {
    if (item.id.includes('@')) {
        const emails = item.id.split(/[\s\,\;]/g).filter(x => !!x && !!x.match(emailPattern)).map(x => x.toLowerCase());
        return emails.map(x => PillableItem(x, x, 'deqo://icons/hero/mail'));
    }
    else {
        return [PillableItem(item.id, item.label)];
    }
};

const useUserSource = (members: UserLiteData[], selection: PillableItem[]) => {

    const api = useResolve(ApiClient);
    const existingLookup = useMemo(() => new Set(([...members, ...selection]).map(x => x.id)), [members, selection]);

    return useCallback(async (query: string) => {
        if (query.includes('@')) {
            query = query.toLowerCase().trim();
            if (query.match(emailPattern)) {
                return Promise.resolve([
                    TypeaheadItem(query, `Invite ${query} (press enter to add)`, existingLookup.has(query) ? 'Already Added' : null, existingLookup.has(query))
                ]);
            }
            else {
                return Promise.resolve([TypeaheadItem('none', 'Type a full email address...', null, true)]);
            }
        }
        else {
            const users = await api.exec(v2.users.list({ like: query, count: 8 }));
            if (users.length) {
                return users.map(x =>
                    TypeaheadItem(x.id, x.name, existingLookup.has(x.id) ? 'Already Added' : null, existingLookup.has(x.id))
                );
            }
            else {
                return Promise.resolve([TypeaheadItem('none', 'No matches, try an email instead.', null, true)]);
            }
        }
    }, [existingLookup]);
};

export const MemberInviteViaEmail = ({ workspaceId, onClose }: { workspaceId: string, onClose(): void }) => {

    const api = useResolve(ApiClient);
    const evts = useResolve(AppEvents);

    const [selection, setSelection] = useState([] as PillableItem[]);

    const [loading, data] = useLoader(async () => {
        const ws = await api.exec(v2.workspaces.infoLite(workspaceId));
        const ml = await api.exec(v2.workspaces.members.list(workspaceId));
        return [ws, ml] as const;
    }, []);


    const [workspace, members] = data ?? [null, []];
    const userSource = useUserSource(members, selection);

    const onSelect = (tm: TypeaheadItem) => {
        setSelection(x => x.concat(toPillables(tm)).slice(0, 50));
        return true;
    };

    const [onInvite, working, result] = useSubmitter(async () => {
        const values = selection.map(x => x.id);
        const results = await api.exec(v2.workspaces.members.add({ workspaceId, values }));
        for (const user of results) {
            evts.push('workspace:members:add', { workspace, user });
        }
        onClose();
    }, [selection, onClose]);

    return <Flexer minHeight="8">
        <LoadingGuard loading={loading}>
            <Flexer flex="column" gap="1">
                <Copy flush>
                    Search for teammates if they&apos;re in another team, or add their emails to send them an invite.
                </Copy>
                <TypeaheadInput
                    autoFocus
                    placeholder="Enter a name or email address"
                    typeaheadSource={userSource}
                    onTypeaheadSelect={onSelect}
                />
                {selection.length > 0 &&
                    <Flexer margin="0 -.25">
                        <FixedScroller maxHeight="24">
                            <PillEditor items={selection} onUpdate={setSelection} config={{
                                canEdit: false,
                                canRemove: true,
                                canAdd: false,
                                canSort: false,
                            }} />
                        </FixedScroller>
                    </Flexer>
                }
                {selection.length >= 50 && <Title size="sm">Maximum 50 invites at a time.</Title>}
                <ResultInfoForSubmitter result={result} />
                <Flexer flex="row" align="center" justify="flex-end">
                    <Button disabled={working || !selection.length} variant="primary" onAction={onInvite}>
                        {working ? 'Add...' : `Add ${!!selection.length ? `(${selection.length})` : ''}`}
                    </Button>
                </Flexer>
            </Flexer>
        </LoadingGuard>
    </Flexer>;
};
